import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import PrintIcon from '@mui/icons-material/Print'
import RestoreIcon from '@mui/icons-material/Restore'
import StarIcon from '@mui/icons-material/Star'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material'
import { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import QueryErrorBoundary from '../components/QueryErrorBoundary'
import { useAppSelector } from '../store/store.hooks'
import SurveyRenderer from './components/SurveyRenderer'
import {
  useQualityTest,
  useRestoreTestExecution,
  useSoftDeleteTestExecution,
  useTestExecution,
} from './services/QualityControlHooks'

interface ExecutionActionButtonsProps {
  testId: string
  executionId: string
}

const ExecutionActionButtons = ({ testId, executionId }: ExecutionActionButtonsProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isAdmin = useAppSelector((s) => s.auth.userDetails?.isAdmin ?? false)
  const { execution } = useTestExecution(executionId)

  const [deleteOpen, setDeleteOpen] = useState(false)
  const [restoreOpen, setRestoreOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const softDelete = useSoftDeleteTestExecution()
  const restore = useRestoreTestExecution()

  let parsedFormResult: Record<string, unknown> = {}
  try {
    parsedFormResult = JSON.parse(execution.formResult) as Record<string, unknown>
  } catch {
    parsedFormResult = {}
  }

  const handleCorrect = () => {
    navigate(`/quality-control/${testId}/execute`, {
      state: {
        initialData: parsedFormResult,
        correctionOfId: execution.id,
        correctionVersionId: execution.qcTestVersionId,
      },
    })
  }

  const handleDelete = async () => {
    try {
      await softDelete.mutateAsync(execution.id)
      setDeleteOpen(false)
      navigate(-1)
    } catch {
      setDeleteOpen(false)
      setErrorMessage(t('qualityControl.deleteExecutionError'))
    }
  }

  const handleRestore = async () => {
    try {
      await restore.mutateAsync(execution.id)
      setRestoreOpen(false)
    } catch {
      setRestoreOpen(false)
      setErrorMessage(t('qualityControl.restoreExecutionError'))
    }
  }

  return (
    <>
      {!execution.isDeleted && (
        <Button variant="contained" size="small" startIcon={<EditIcon />} onClick={handleCorrect}>
          {t('qualityControl.correct')}
        </Button>
      )}
      {!execution.isDeleted && (
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<DeleteOutlineIcon />}
          onClick={() => setDeleteOpen(true)}
        >
          {t('qualityControl.deleteExecution')}
        </Button>
      )}
      {execution.isDeleted && isAdmin && (
        <Button
          variant="outlined"
          size="small"
          color="success"
          startIcon={<RestoreIcon />}
          onClick={() => setRestoreOpen(true)}
        >
          {t('qualityControl.restoreExecution')}
        </Button>
      )}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('qualityControl.deleteExecutionConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('qualityControl.deleteExecutionConfirmBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t('qualityControl.cancel')}</Button>
          <Button color="error" onClick={handleDelete} disabled={softDelete.isPending}>
            {softDelete.isPending ? t('qualityControl.deleting') : t('qualityControl.deleteExecution')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={restoreOpen} onClose={() => setRestoreOpen(false)}>
        <DialogTitle>{t('qualityControl.restoreExecutionConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('qualityControl.restoreExecutionConfirmBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreOpen(false)}>{t('qualityControl.cancel')}</Button>
          <Button color="success" onClick={handleRestore} disabled={restore.isPending}>
            {restore.isPending ? t('qualityControl.restoring') : t('qualityControl.restoreExecution')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={errorMessage !== null} autoHideDuration={5000} onClose={() => setErrorMessage(null)}>
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

interface TestExecutionViewerContentProps {
  testId: string
  executionId: string
}

const TestExecutionViewerContent = ({ testId, executionId }: TestExecutionViewerContentProps) => {
  const { t } = useTranslation()
  const { qualityTest } = useQualityTest(testId)
  const { execution } = useTestExecution(executionId)

  let parsedFormResult: Record<string, unknown> = {}
  try {
    parsedFormResult = JSON.parse(execution.formResult) as Record<string, unknown>
  } catch {
    parsedFormResult = {}
  }

  return (
    <>
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, sm: 8 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {qualityTest.displayName}
            </Typography>
            {qualityTest.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {qualityTest.description}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {execution.isDeleted && <Chip label={t('qualityControl.deleted')} size="small" color="error" />}
              {execution.correctionOfId && (
                <Chip label={t('qualityControl.correctionBadge')} size="small" color="info" />
              )}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'flex-start', sm: 'flex-end' },
                gap: 1,
              }}
            >
              {qualityTest.category && (
                <Chip
                  label={qualityTest.category}
                  size="small"
                  sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText' }}
                />
              )}
              <Chip
                label={new Date(execution.executionDate).toLocaleDateString()}
                size="small"
                variant="outlined"
                icon={<CalendarTodayIcon />}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonOutlineIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {execution.observedBy}
                </Typography>
              </Box>
              <Chip
                label={`${t('qualityControl.score')}: ${execution.score}`}
                size="small"
                icon={<StarIcon />}
                sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          borderTop: '4px solid',
          borderColor: execution.isDeleted ? 'error.main' : 'primary.main',
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 1,
          p: 2,
          opacity: execution.isDeleted ? 0.65 : 1,
        }}
      >
        <SurveyRenderer formJson={execution.formJson} mode="display" initialData={parsedFormResult} />
      </Box>
    </>
  )
}

const TestExecutionViewer = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { testId, executionId } = useParams<{ testId: string; executionId: string }>()

  if (!testId || !executionId) {
    return <Navigate to="/quality-control" replace />
  }

  return (
    <Box sx={{ p: 3 }}>
      <QueryErrorBoundary>
        <Box sx={{ display: 'flex', gap: 1, mb: 2, '@media print': { display: 'none' } }}>
          <Button variant="contained" size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            {t('qualityControl.backToHistory')}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            sx={{ ml: 'auto' }}
          >
            {t('qualityControl.print')}
          </Button>
          <Suspense fallback={null}>
            <ExecutionActionButtons testId={testId} executionId={executionId} />
          </Suspense>
        </Box>
        <Suspense
          fallback={
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
              <CircularProgress color="secondary" size={75} />
            </Box>
          }
        >
          <TestExecutionViewerContent testId={testId} executionId={executionId} />
        </Suspense>
      </QueryErrorBoundary>
    </Box>
  )
}

export default TestExecutionViewer
